import { createContainer } from 'unstated-next'
import { useEffect, useMemo, useState } from 'react'
import { useFilter } from '../common/useFilter'
import { ChannelCustomerSegment, Company, MarketSalesFilters, Site, SupplyProduct, StringDate } from './models'
import { api } from '../../infrastructure/api'
import moment from 'moment'
import { StockFiltersContainer } from '../stock/filters/filtersStore'
import { setInLocalStorage } from '../../infrastructure/localStorage'
import { hasFeature } from '../../infrastructure/feature'

const defaultStartDate = () => moment().startOf('month').format('YYYY-MM-DD')
const defaultEndDate = () => moment().endOf('month').format('YYYY-MM-DD')

function useMktSalesFilters() {
    let stockFilters = StockFiltersContainer.useContainer()

    let [startDate, setStartDate] = useState<StringDate | null>(defaultStartDate())
    let [endDate, setEndDate] = useState<StringDate | null>(defaultEndDate())
    let [useSiteInPercentageCompute, setUseSiteInPercentageCompute] = useState<boolean>(false)

    let companiesFilter = useFilter<Company>(companies => companies.map(x => ({ value: x.code, text: x.name })))
    let customerSegmentsFilter =
        useFilter<ChannelCustomerSegment>(customerSegments => deepDistinct(customerSegments.map(x => ({ value: x.customerSegment, text: x.customerSegment, group: x.channel }))))
    
    let [loadedSignal, setLoadedSignal] = useState<{}>({})

    let values = useMemo(() => {
        return {
            startDate,
            endDate,
            useSiteInPercentageCompute,
            companies: companiesFilter.values(),
            customerSegments: customerSegmentsFilter.values(),
            sites: stockFilters.filters.sitesFilter.values(),
            productIds: stockFilters.filters.productIdsFilter.values(),
            loadedSignal
        }
    }, [
        startDate,
        endDate,
        useSiteInPercentageCompute,
        companiesFilter.selectedChoices,
        customerSegmentsFilter.selectedChoices,
        stockFilters.filters.sitesFilter.selectedChoices,
        stockFilters.filters.productIdsFilter.selectedChoices,
        loadedSignal
    ])

    useEffect(() => {
        if (customerSegmentsFilter.items.length > 0) saveFiltersInLocalStorage()
    }, [values])

    let saveFiltersInLocalStorage = () => {
        let filters: MarketSalesFilters = {
            startDate: startDate,
            endDate: endDate,
            customerSegments: customerSegmentsFilter.selectedChoices
        }
        setInLocalStorage('mktSalesFilters_v2', filters)
    }

    async function loadFilters() {
        let products = await api.get<SupplyProduct[]>('stock/product')
        let sites = await api.get<Site[]>('stock/site')

        let channelCustomerSegments = await api.get<ChannelCustomerSegment[]>('stock/movement/mktSale/channelCustomerSegment')
        let companies = await api.get<Company[]>('stock/company')

        stockFilters.loadFiltersElements(products, sites, companies)
        let storedFilters : MarketSalesFilters | null = loadDatesFromLocalStorage()

        let storedCustomerSegments = storedFilters?.customerSegments ?? []
        let storedCustomerSegmentsValues = storedCustomerSegments.length === 0
                ? channelCustomerSegments.map(x => x.customerSegment).distinct()
                : storedCustomerSegments
        
        let companiesWithCustomerSegment = companies.filter(x => channelCustomerSegments.some(ccs => ccs.company === x.code))
        let selectedCompaniesInStockPage = stockFilters.filters.companiesFilter.values().filter(x => companiesWithCustomerSegment.some(c => c.code === x))
        
        let availableCompanies = getAvailableCompanies(storedCustomerSegmentsValues, channelCustomerSegments)
        let selectedCompaniesWithinAvailable = selectedCompaniesInStockPage.filter(x => availableCompanies.includes(x))
        
        if (selectedCompaniesWithinAvailable.length === 0) {
            companiesFilter.setItems(companies,  (item: Company) => companiesWithCustomerSegment.map(x => x.code).includes(item.code))
            companiesFilter.setSelectedChoices(companiesWithCustomerSegment.length === 1
                    ? [companiesWithCustomerSegment[0].code]
                    : [])
            customerSegmentsFilter.setItems(channelCustomerSegments)
            customerSegmentsFilter.setSelectedChoices([])
        }
        else {
            companiesFilter.setItems(companiesWithCustomerSegment, (item: Company) => availableCompanies.includes(item.code))
            companiesFilter.setSelectedChoices(availableCompanies.length === 1 
                    ? [availableCompanies[0]] 
                    : availableCompanies.length === selectedCompaniesWithinAvailable.length 
                            ? []
                            : selectedCompaniesWithinAvailable)

            let availableCustomerSegments = getAvailableCustomerSegments(selectedCompaniesWithinAvailable, channelCustomerSegments)
            let newSelectedCustomerSegments = storedCustomerSegmentsValues.filter(x => availableCustomerSegments.includes(x))
            customerSegmentsFilter.setItems(channelCustomerSegments, (item: ChannelCustomerSegment) => availableCustomerSegments.includes(item.customerSegment))
            customerSegmentsFilter.setSelectedChoices(newSelectedCustomerSegments.length === availableCustomerSegments.length ? [] : newSelectedCustomerSegments)
        }
        setUseSiteInPercentageCompute(hasFeature('MktSalesDefaultSitePercent'))
        setLoadedSignal({})
    }

    function loadDatesFromLocalStorage() : MarketSalesFilters | null {
        let filters = localStorage.getItem('mktSalesFilters_v2')
        let filterObj: MarketSalesFilters | null = null;

        if (filters) {
            filterObj = JSON.parse(filters)

            if (filterObj!.startDate) setStartDate(filterObj!.startDate)
            if (filterObj!.endDate) setEndDate(filterObj!.endDate)
        }
        return !!filters ? filterObj : null
    }
    
    let getAvailableCompanies = (selectedCustomerSegments: string[], allCustomerSegments: ChannelCustomerSegment[]) => {
        return allCustomerSegments
                .filter(x => selectedCustomerSegments.includes(x.customerSegment))
                .map(x => x.company)
                .distinct()
    }

    let getAvailableCustomerSegments = (selectedCompanies: string[], allCustomerSegments: ChannelCustomerSegment[]) => {
        return allCustomerSegments
                .filter(x => selectedCompanies.includes(x.company))
                .map(x => x.customerSegment).distinct()
    }

    let handleCustomerSegmentSelection = (newSelectedCustomerSegments: string[]) => {
        let newCustomerSegmentsValues = newSelectedCustomerSegments.length === 0 
                ? customerSegmentsFilter.choices.map(x => x.value) 
                : newSelectedCustomerSegments
        let availableCompanies =  getAvailableCompanies(newCustomerSegmentsValues, customerSegmentsFilter.items)
        let selectedCompaniesWithinAvailable = companiesFilter.values().filter(x => availableCompanies.includes(x))
        let availableCustomerSegments = getAvailableCustomerSegments(selectedCompaniesWithinAvailable, customerSegmentsFilter.items)
        
        setAvailableAsFilterChoices(availableCompanies, availableCustomerSegments)
        customerSegmentsFilter.setSelectedChoices(newSelectedCustomerSegments)
    }
    
    let handleCompanySelection = (newSelectedCompanies: string[]) => {
        let newCompaniesValues = newSelectedCompanies.length === 0 
                ? companiesFilter.choices.map(x => x.value) 
                : newSelectedCompanies
        let availableCustomerSegments =  getAvailableCustomerSegments(newCompaniesValues, customerSegmentsFilter.items)
        let selectedCustomerSegmentsWithinAvailable = customerSegmentsFilter.values().filter(x => availableCustomerSegments.includes(x))
        let availableCompanies = getAvailableCompanies(selectedCustomerSegmentsWithinAvailable, customerSegmentsFilter.items)
        
        setAvailableAsFilterChoices(availableCompanies, availableCustomerSegments)
        companiesFilter.setSelectedChoices(newSelectedCompanies)
    }

    function setAvailableAsFilterChoices(availableCompanies: string[], availableCustomerSegments: string[]) {
        let shouldChangeCompaniesChoices = !areEqual(availableCompanies, companiesFilter.choices.map(x => x.value))
        let shouldChangeCustomerSegmentChoices = !areEqual(availableCustomerSegments, customerSegmentsFilter.choices.map(x => x.value))

        if (shouldChangeCompaniesChoices) companiesFilter.setChoicesFromFilteredItems((item: Company) => availableCompanies.includes(item.code))
        if (shouldChangeCustomerSegmentChoices) 
            customerSegmentsFilter.setChoicesFromFilteredItems((item: ChannelCustomerSegment) => availableCustomerSegments.includes(item.customerSegment))
    }

    return {
        startDate, setStartDate,
        endDate, setEndDate,
        useSiteInPercentageCompute, setUseSiteInPercentageCompute,
        customerSegmentsFilter, companiesFilter,
        productIdsFilter: stockFilters.filters.productIdsFilter,
        sitesFilter: stockFilters.filters.sitesFilter,
        loadFilters, selectedValues: values, 
        handleCustomerSegmentSelection, handleCompanySelection
    }
}

function deepDistinct<T>(array: T[]): T[] {
    return array.map(x => JSON.stringify(x)).distinct().map(x => JSON.parse(x))
}

function areEqual(array1: string[], array2: string[]) {
    if (array1.length !== array2.length) return false
    let set1 = new Set(array1)
    let set2 = new Set(array2)
    let intersection = new Set()
    set1.forEach(x => { if (set2.has(x)) intersection.add(x) })
    return intersection.size === set1.size && set2.size === set1.size
}

export let MktSalesFiltersContainer = createContainer(useMktSalesFilters)